import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AlertaStatus, ConfigAlcadaNivel, EmpresaTenant, ScopeType, UsuarioWorkflow } from './models';
import workflowService from './workflowService';
import { usuariosSelecionaveis } from './usuariosSelecionaveis';
import { empresasElegiveisParaEscopo, empresaSemVinculoValido } from './empresasElegiveis';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { justificativaAlcada } from './justificativaSox';
import api from '../../services/Api';
import { formatarCnpj } from '../../utils/String';

interface IntegridadeNivel {
  nivel: number;
  nomeCargo: string;
  totalUsuarios: number;
  temTitular: boolean;
  temSubstituto: boolean;
  alertaStatus: 'OK' | 'ALERTA' | 'ERRO';
  valorMinimo?: number;
  valorMaximo?: number;
  slaDiasUteis?: number;
}

const money = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const statusIcon = (s: AlertaStatus) => (s === 'OK' ? '●' : s === 'ALERTA' ? '▲' : '✕');
const statusClass = (s: AlertaStatus) =>
  s === 'OK' ? 'wf-alc-ok' : s === 'ALERTA' ? 'wf-alc-alerta' : 'wf-alc-erro';
const scopeTypeLabel: Record<ScopeType, string> = {
  GROUP: 'Grupo Total',
  SUBGROUP: 'Subgrupo',
  SINGLE: 'Empresa Individual',
};

const NIVEIS_CARGO = [
  { nivel: 1, cargo: 'Analista Contábil' },
  { nivel: 2, cargo: 'Supervisor Contábil' },
  { nivel: 3, cargo: 'Coordenador Contábil' },
  { nivel: 4, cargo: 'Gerente Contábil' },
  { nivel: 5, cargo: 'Diretor de Controladoria' },
  { nivel: 6, cargo: 'CFO' },
  { nivel: 7, cargo: 'CEO' },
];

const ConfigAlcadasPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ fromOnboarding?: boolean; etapa?: number }>();
  const fromOnboarding = location.state?.fromOnboarding ?? false;
  const onboardingEtapa = location.state?.etapa ?? 2;

  const [alcadas, setAlcadas] = useState<ConfigAlcadaNivel[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioWorkflow[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [cargos, setCargos] = useState<{ cargoId: number; nivelHierarquico: number }[]>([]);
  const [etapasSla, setEtapasSla] = useState<{ etapaId: number; nomeEtapa: string; ordemSequencial: number; slaDiasUteis: number | null }[]>([]);
  const [editandoEtapaId, setEditandoEtapaId] = useState<number | null>(null);
  const [slaEtapaForm, setSlaEtapaForm] = useState('');
  const [loading, setLoading] = useState(true);

  const [integridade, setIntegridade] = useState<IntegridadeNivel[]>([]);

  const [step, setStep] = useState(1);
  const [scopeType, setScopeType] = useState<ScopeType>('GROUP');
  const [scopeTenantIds, setScopeTenantIds] = useState<number[]>([]);
  const [selectedNivel, setSelectedNivel] = useState(1);
  const [valorMin, setValorMin] = useState('');
  const [valorMax, setValorMax] = useState('');
  const [sla, setSla] = useState('');
  const [titularId, setTitularId] = useState<number | ''>('');
  const [substitutoId, setSubstitutoId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [confirmaAlerta, setConfirmaAlerta] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState<number | null>(null);
  const [excluindoId, setExcluindoId] = useState<number | null>(null);
  // Justificativa SOX já sugerida (nível, escopo, faixa de valor e SLA); a pessoa revisa e edita se quiser.
  const escopoSugerido = scopeType === 'GROUP'
    ? 'todo o grupo'
    : empresas.filter(e => scopeTenantIds.includes(e.id)).map(e => e.nome).join(', ') || (scopeType === 'SUBGROUP' ? 'subgrupo de empresas' : 'empresa');
  // Seletor de escopo: só empresas ativas vinculadas a grupo (mantém as já gravadas na alçada em edição)
  const empresasParaEscopo = empresasElegiveisParaEscopo(empresas, scopeTenantIds);
  const justificativa = useJustificativaSugerida(
    justificativaAlcada({ nivel: selectedNivel, escopo: escopoSugerido, minimo: valorMin, maximo: valorMax, slaDias: sla }));

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 5000);
  };

  const fetchDados = () => {
    setLoading(true);
    Promise.all([
      workflowService.listarAlcadas(),
      workflowService.listarUsuarios(),
      workflowService.listarTenants(),
      workflowService.listarCargos(),
      workflowService.listarEtapasSla(),
    ])
      .then(([a, u, e, c, es]) => {
        setAlcadas(a);
        setUsuarios(u);
        setEmpresas(e);
        setCargos(c);
        setEtapasSla(es);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('workflow/config/alcadas/integridade').then(r => setIntegridade(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchDados();
  }, []);

  const toggleScopeTenant = (id: number) =>
    setScopeTenantIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const nivelSelecionado = NIVEIS_CARGO.find(n => n.nivel === selectedNivel) ?? NIVEIS_CARGO[0];
  const titularAtual = usuarios.find(u => u.nivel === selectedNivel && u.ativo);

  const alertas: string[] = [];
  if (!substitutoId) alertas.push('Atenção: Não há aprovador substituto. Em caso de ausência do titular, o fluxo será travado.');
  if (valorMin && valorMax && Number(valorMin) > Number(valorMax))
    alertas.push('Atenção: Valor mínimo superior ao máximo. Revise a configuração.');
  if (scopeType === 'SUBGROUP' && scopeTenantIds.length < 2)
    alertas.push('SUBGROUP requer pelo menos 2 empresas selecionadas.');

  // Alerta 4 — sem titular operacional (nenhum usuário ativo com esse nível)
  const niveisComUsuario = new Set(usuarios.filter(u => u.ativo).map(u => u.nivel));
  if (!niveisComUsuario.has(selectedNivel))
    alertas.push('Atenção: Nenhum usuário ativo encontrado para este nível hierárquico. Esta alçada não terá executor operacional.');

  // Alerta 5 — inconsistência hierárquica (valorMin deste nível < valorMax do nível anterior)
  const alcadaNivelAnterior = alcadas.find(a => a.nivel === selectedNivel - 1);
  if (alcadaNivelAnterior && valorMin && alcadaNivelAnterior.valorMax && Number(valorMin) < alcadaNivelAnterior.valorMax)
    alertas.push(`Atenção: Inconsistência hierárquica — o valor mínimo (${valorMin}) é inferior ao valor máximo do nível ${selectedNivel - 1} (${alcadaNivelAnterior.valorMax}). Revise a coerência das alçadas.`);

  // Alerta 6 — conflito de escopo SINGLE
  if (scopeType === 'SINGLE' && scopeTenantIds.length > 0) {
    const conflito = alcadas.find(a => a.scopeType === 'SINGLE' && a.nivel === selectedNivel);
    if (conflito)
      alertas.push('Atenção: Já existe uma regra Individual definida para este nível. Esta configuração irá sobrescrever a anterior.');
  }

  const temAlertaCritico = alertas.some(a => a.startsWith('Atenção:'));

  const podeAvancarStep1 = scopeType === 'GROUP' || (scopeType === 'SUBGROUP' ? scopeTenantIds.length >= 2 : scopeTenantIds.length >= 1);

  // A etapa "Aprovação" é sempre a etapa_id=5 (seed fixo em wf_etapas) — é ela quem
  // resolve a escada de alçada N1-N7 por faixa de valor; as demais etapas do processo
  // (Cadastro, Evidências, Conciliação, Revisão, Encerramento) não usam essa escada.
  const ETAPA_APROVACAO_ID = 5;

  const cargoIdSelecionado = cargos.find(c => c.nivelHierarquico === selectedNivel)?.cargoId;

  const salvarSlaEtapa = (etapaId: number) => {
    const dias = Number(slaEtapaForm);
    if (!dias || dias < 0) return;
    workflowService.atualizarSlaEtapa(etapaId, dias, 'Ajuste da matriz de SLA do fechamento (D+5).').then(() => {
      setEtapasSla(prev => prev.map(e => e.etapaId === etapaId ? { ...e, slaDiasUteis: dias } : e));
      setEditandoEtapaId(null);
      showToast('SLA da etapa atualizado.');
    }).catch(() => showToast('Erro ao atualizar SLA da etapa.'));
  };

  const resetForm = () => {
    setStep(1);
    justificativa.restaurar();
    setScopeType('GROUP');
    setScopeTenantIds([]);
    setValorMin('');
    setValorMax('');
    setSla('');
    setTitularId('');
    setSubstitutoId('');
    setConfirmaAlerta(false);
    setEditingConfigId(null);
  };

  const iniciarEdicao = (a: ConfigAlcadaNivel) => {
    setEditingConfigId(a.id);
    setScopeType(a.scopeType);
    setScopeTenantIds(a.scopeTenantIds);
    setSelectedNivel(a.nivel);
    setValorMin(a.valorMin ? String(a.valorMin) : '');
    setValorMax(a.valorMax != null ? String(a.valorMax) : '');
    setSla(String(a.slaDiasUteis));
    setTitularId(a.titularId ?? '');
    setSubstitutoId(a.substitutoId ?? '');
    justificativa.restaurar();
    setConfirmaAlerta(false);
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const excluir = (a: ConfigAlcadaNivel) => {
    const aviso = a.titular !== '—'
      ? ` Há uma aprovação em andamento que pode depender desta alçada (titular: ${a.titular}).`
      : '';
    if (!window.confirm(`Excluir a alçada N${a.nivel} — ${a.cargo} (${a.scopeLabel || a.scopeType})?${aviso} Esta ação não pode ser desfeita.`)) return;

    setExcluindoId(a.id);
    workflowService.excluirAlcada(a.id)
      .then(() => {
        if (editingConfigId === a.id) resetForm();
        fetchDados();
        showToast('Alçada excluída. Registro de auditoria SOX gerado.');
      })
      .catch(() => showToast('Erro ao excluir a alçada.'))
      .finally(() => setExcluindoId(null));
  };

  const salvar = () => {
    if (!justificativa.texto.trim()) return;
    if (scopeType === 'SUBGROUP' && scopeTenantIds.length < 2) return;
    if (!cargoIdSelecionado) {
      showToast(`Não existe o cargo N${selectedNivel} cadastrado. Cadastre-o primeiro em Cadastro de Cargos.`);
      return;
    }
    setSaving(true);

    const dto = {
      etapaId: ETAPA_APROVACAO_ID,
      cargoId: cargoIdSelecionado,
      scopeType,
      scopeTenantIds: scopeType !== 'GROUP' ? scopeTenantIds : undefined,
      valorMinimo: valorMin ? Number(valorMin) : undefined,
      valorMaximo: valorMax ? Number(valorMax) : undefined,
      slaDiasUteis: sla ? Number(sla) : 1,
      titularId: titularId !== '' ? titularId : undefined,
      substitutoId: substitutoId !== '' ? substitutoId : undefined,
      justificativaSox: justificativa.texto.trim(),
    };

    const request = editingConfigId != null
      ? workflowService.atualizarAlcada(editingConfigId, dto)
      : workflowService.salvarAlcada(dto);
    const eraEdicao = editingConfigId != null;

    request
      .then(() => {
        resetForm();
        fetchDados();
        showToast(eraEdicao
          ? 'Alçada atualizada. Registro de auditoria SOX gerado.'
          : 'Registro de auditoria SOX gerado com sucesso. Alçada persistida.');
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <div className='wf-page'>
      <div className='wf-breadcrumb'>Home &rsaquo; Governança de TI &rsaquo; Configuração de Alçadas</div>
      <div className='wf-header'>
        <div>
          <h1>Configuração de Alçadas</h1>
          <p>Wizard para parametrização de escopo, cargos, valores e SLA com auditoria SOX.</p>
        </div>
        <div className='wf-header__actions'>
          {fromOnboarding && (
            <button
              className='wf-btn wf-btn--secondary'
              onClick={() => history.push('/workflow-aprovacao/onboarding', { goToEtapa: onboardingEtapa })}
            >
              ← Onboarding Etapa {onboardingEtapa}
            </button>
          )}
          {!fromOnboarding && <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>}
          <span className='wf-level7'>Acesso exclusivo Nível 7 — TI</span>
        </div>
      </div>

      {toast && <div className='wf-toast wf-toast--success'><strong>SOX</strong> {toast}</div>}

      <div className='wf-grid-2' style={{ marginBottom: 0 }}>
        <section className='wf-card'>
          {/* Step indicator */}
          <div className='wf-wizard-steps'>
            {['Escopo', 'Cargo', 'Alçadas', 'Validação'].map((name, i) => (
              <React.Fragment key={i}>
                <div className={`wf-wizard-step ${step === i + 1 ? 'is-active' : step > i + 1 ? 'is-done' : ''}`}>
                  <span>{step > i + 1 ? '✓' : i + 1}</span><strong>{name}</strong>
                </div>
                {i < 3 && <div className={`wf-wizard-line ${step > i + 1 ? 'is-done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>

          {editingConfigId != null && (
            <div className='wf-review' style={{ marginTop: 12 }}>
              <strong>Editando alçada existente (ID {editingConfigId})</strong>
              <p>
                Ao concluir, as alterações serão salvas nesta mesma configuração.
                {' '}
                <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={resetForm}>Cancelar edição</button>
              </p>
            </div>
          )}

          {/* Step 1 — Escopo */}
          {step === 1 && (
            <div className='wf-form-grid wf-form-grid--one' style={{ marginTop: 20 }}>
              <label>
                Tipo de escopo
                {(['GROUP', 'SUBGROUP', 'SINGLE'] as ScopeType[]).map(st => (
                  <label key={st} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', cursor: 'pointer', fontWeight: 400 }}>
                    <input type='radio' name='scope' checked={scopeType === st} onChange={() => { setScopeType(st); setScopeTenantIds([]); }} />
                    <div>
                      <strong>{scopeTypeLabel[st]}</strong>
                      <span style={{ display: 'block', fontSize: 10, color: '#a1a5b7' }}>
                        {st === 'GROUP'
                          ? 'Aplica a todas as empresas do grupo. Herança automática.'
                          : st === 'SUBGROUP'
                          ? 'Selecione ao menos 2 empresas específicas do grupo.'
                          : 'Regra exclusiva para uma empresa. Prioridade máxima.'}
                      </span>
                    </div>
                  </label>
                ))}
              </label>
              {scopeType !== 'GROUP' && (
                <label>
                  Selecione {scopeType === 'SUBGROUP' ? 'as empresas (mínimo 2)' : 'a empresa'}
                  <div className='wf-multiselect'>
                    {empresasParaEscopo.length === 0 && (
                      <div className='wf-review' data-testid='sem-empresas-elegiveis' style={{ margin: 6 }}>
                        <strong>Nenhuma empresa elegível</strong>
                        <p style={{ margin: '4px 0 0' }}>
                          Só aparecem empresas vinculadas a um grupo econômico. Conclua o cadastro dos grupos e o vínculo das empresas
                          (Etapa 1 do onboarding) e volte aqui.{' '}
                          <button type='button' className='wf-link' onClick={() => history.push('/workflow-aprovacao/cadastro-grupos')}>Abrir cadastro de grupos</button>
                        </p>
                      </div>
                    )}
                    {empresasParaEscopo.map(e => (
                      <label
                        key={e.id}
                        className={`wf-multiselect__item ${scopeTenantIds.includes(e.id) ? 'is-selected' : ''}`}
                        onClick={() => {
                          if (scopeType === 'SINGLE') {
                            setScopeTenantIds([e.id]);
                          } else {
                            toggleScopeTenant(e.id);
                          }
                        }}
                      >
                        <span className='wf-multiselect__check'>{scopeTenantIds.includes(e.id) ? '✓' : ''}</span>
                        <div><strong>{e.nome}</strong><span>{empresaSemVinculoValido(e) ? 'Sem grupo econômico — revise o vínculo' : formatarCnpj(e.cnpj)}</span></div>
                      </label>
                    ))}
                  </div>
                </label>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className='wf-btn wf-btn--primary'
                  disabled={!podeAvancarStep1}
                  onClick={() => setStep(2)}
                >
                  Próximo
                </button>
              </div>
            </div>
          )}

          {/* Step 2 — Cargo */}
          {step === 2 && (
            <div className='wf-form-grid wf-form-grid--one' style={{ marginTop: 20 }}>
              <label>
                Nível hierárquico
                <select value={selectedNivel} onChange={e => setSelectedNivel(Number(e.target.value))}>
                  {NIVEIS_CARGO.map(n => (
                    <option key={n.nivel} value={n.nivel}>N{n.nivel} — {n.cargo}</option>
                  ))}
                </select>
              </label>
              <label>
                Titular da alçada
                <select value={titularId} onChange={e => setTitularId(e.target.value === '' ? '' : Number(e.target.value))}>
                  <option value=''>— Selecione titular —</option>
                  {usuariosSelecionaveis(usuarios.filter(u => u.nivel === selectedNivel && u.ativo), [titularId]).map(u => (
                    <option key={u.id} value={u.id}>{u.nome} — {u.cargo}</option>
                  ))}
                </select>
              </label>
              {titularAtual && (
                <div className='wf-alc-preview'>
                  <strong>Sugestão de titular:</strong> {titularAtual.nome}<br />
                  <strong>Cargo:</strong> {titularAtual.cargo}<br />
                  <strong>Empresas vinculadas:</strong> {titularAtual.empresaIds.length} empresa(s)
                  {titularAtual.ativo
                    ? <span className='wf-badge wf-badge--aprovada' style={{ marginLeft: 8 }}>Ativo</span>
                    : <span className='wf-badge wf-badge--rejeitada' style={{ marginLeft: 8 }}>Inativo</span>}
                </div>
              )}
              {!titularAtual && (
                <div className='wf-review'>
                  <strong>Alerta de Responsável Órfão</strong>
                  <p>Nenhum usuário encontrado com este nível. Esta alçada não terá executor operacional.</p>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className='wf-btn wf-btn--secondary' onClick={() => setStep(1)}>Voltar</button>
                <button className='wf-btn wf-btn--primary' onClick={() => setStep(3)}>Próximo</button>
              </div>
            </div>
          )}

          {/* Step 3 — Alçadas */}
          {step === 3 && (
            <div className='wf-form-grid' style={{ marginTop: 20 }}>
              <label>
                Valor mínimo (R$)
                <input type='number' value={valorMin} onChange={e => setValorMin(e.target.value)} placeholder='0' />
              </label>
              <label>
                Valor máximo (R$)
                <input type='number' value={valorMax} onChange={e => setValorMax(e.target.value)} placeholder='Sem limite' />
              </label>
              <label>
                SLA (dias úteis)
                <input type='number' value={sla} onChange={e => setSla(e.target.value)} placeholder='Ex: 2' />
              </label>
              <label>
                Aprovador substituto
                <select value={substitutoId} onChange={e => setSubstitutoId(e.target.value === '' ? '' : Number(e.target.value))}>
                  <option value=''>— Selecione —</option>
                  {usuariosSelecionaveis(usuarios.filter(u => u.nivel === selectedNivel && u.ativo && u.id !== titularId), [substitutoId]).map(u => (
                    <option key={u.id} value={u.id}>{u.nome} — {u.cargo}</option>
                  ))}
                </select>
                <small style={{ color: '#a1a5b7' }}>O substituto deve pertencer ao mesmo grupo/cargo do titular (N{selectedNivel}).</small>
              </label>
              <div style={{ gridColumn: '1/-1', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className='wf-btn wf-btn--secondary' onClick={() => setStep(2)}>Voltar</button>
                <button className='wf-btn wf-btn--primary' onClick={() => setStep(4)}>Próximo</button>
              </div>
            </div>
          )}

          {/* Step 4 — Validação */}
          {step === 4 && (
            <div className='wf-form-grid wf-form-grid--one' style={{ marginTop: 20 }}>
              <div className='wf-alc-preview'>
                <strong>Resumo da configuração</strong><br />
                Escopo: <strong>{scopeTypeLabel[scopeType]}</strong>
                {scopeType !== 'GROUP' && scopeTenantIds.length > 0 && (
                  <span> — {scopeTenantIds.length} empresa(s) selecionada(s)</span>
                )}
                &nbsp;|&nbsp;
                Nível: <strong>N{selectedNivel} — {nivelSelecionado.cargo}</strong><br />
                Valor: <strong>{valorMin ? money(Number(valorMin)) : 'R$ 0'}</strong> a{' '}
                <strong>{valorMax ? money(Number(valorMax)) : 'Sem limite'}</strong> &nbsp;|&nbsp;
                SLA: <strong>{sla || '—'} dia(s) útil(eis)</strong> &nbsp;|&nbsp;
                Substituto: <strong>{substitutoId ? (usuarios.find(u => u.id === substitutoId)?.nome ?? '—') : '—'}</strong>
              </div>
              {alertas.map((a, i) => (
                <div key={i} className='wf-review'><strong>Alerta de Governança</strong><p>{a}</p></div>
              ))}
              <label>
                Justificativa SOX <span style={{ color: '#f64e60', fontSize: 10 }}>(obrigatória para salvar)</span>
                <CampoJustificativaSox
                  justificativa={justificativa}
                  rows={4}
                  placeholder='Descreva o motivo da configuração para fins de auditoria SOX...'
                />
              </label>
              {temAlertaCritico && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, fontWeight: 600, color: '#f64e60', cursor: 'pointer' }}>
                  <input
                    type='checkbox'
                    checked={confirmaAlerta}
                    onChange={e => setConfirmaAlerta(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  Estou ciente dos alertas e confirmo o salvamento com justificativa SOX
                </label>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className='wf-btn wf-btn--secondary' onClick={() => setStep(3)}>Voltar</button>
                <button
                  className='wf-btn wf-btn--primary'
                  disabled={saving || !justificativa.texto.trim() || (scopeType === 'SUBGROUP' && scopeTenantIds.length < 2) || (temAlertaCritico && !confirmaAlerta)}
                  onClick={salvar}
                >
                  {saving ? 'Salvando...' : editingConfigId != null ? 'Atualizar e registrar SOX' : 'Salvar e registrar SOX'}
                </button>
              </div>
            </div>
          )}
        </section>

        <section className='wf-card'>
          <div className='wf-card__header'><div><h2>Mapa de Escopos</h2><p>Visão hierárquica das regras por nível.</p></div></div>
          {loading ? (
            <p className='wf-count'>Carregando...</p>
          ) : (
            <div className='wf-scope-tree'>
              {NIVEIS_CARGO.map(n => {
                const alcada = alcadas.find(a => a.nivel === n.nivel);
                return (
                  <div key={n.nivel} className='wf-scope-tree__group'>
                    <div className='wf-scope-tree__group-label'>
                      <strong>N{n.nivel} — {n.cargo}</strong>
                      {alcada && (
                        <span className='wf-badge wf-badge--em_analise' style={{ fontSize: 9, marginLeft: 6 }}>
                          {scopeTypeLabel[alcada.scopeType]}
                        </span>
                      )}
                    </div>
                    {alcada && (
                      <div className='wf-scope-tree__empresa'>
                        <span>Titular: {alcada.titular}</span>
                        <span className={`wf-alc-status ${statusClass(alcada.status)}`} style={{ fontSize: 10 }}>
                          {statusIcon(alcada.status)} {alcada.status}
                        </span>
                      </div>
                    )}
                    {!alcada && (
                      <div className='wf-scope-tree__empresa'>
                        <span style={{ color: '#a1a5b7', fontSize: 10 }}>Não configurado</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <section className='wf-card' style={{ marginTop: 20 }}>
        <div className='wf-card__header'><div><h2>Alçadas configuradas</h2><p>Status atual por nível hierárquico.</p></div></div>
        {loading ? (
          <p className='wf-count' style={{ textAlign: 'center', padding: 20 }}>Carregando...</p>
        ) : (
          <div className='wf-table-wrap'>
            <table className='wf-table'>
              <thead>
                <tr>
                  <th>Status</th><th>Nível / Cargo</th><th>Escopo</th>
                  <th>Valor mín.</th><th>Valor máx.</th><th>SLA</th>
                  <th>Titular</th><th>Substituto</th><th />
                </tr>
              </thead>
              <tbody>
                {alcadas.length === 0 && (
                  <tr><td colSpan={9} className='wf-count'>Nenhuma alçada configurada.</td></tr>
                )}
                {alcadas.map(a => (
                  <tr key={a.id}>
                    <td><span className={`wf-alc-status ${statusClass(a.status)}`}>{statusIcon(a.status)} {a.status}</span></td>
                    <td>
                      <strong>N{a.nivel} — {a.cargo}</strong>
                      {a.alertas.map((al, i) => <p key={i} style={{ fontSize: 9, color: '#ffa800', margin: '2px 0 0' }}>⚠ {al}</p>)}
                    </td>
                    <td>
                      <span className='wf-badge wf-badge--em_analise' style={{ fontSize: 9 }}>{scopeTypeLabel[a.scopeType]}</span>
                      <span style={{ display: 'block', fontSize: 10, color: '#a1a5b7' }}>{a.scopeLabel}</span>
                    </td>
                    <td>{money(a.valorMin)}</td>
                    <td>{a.valorMax ? money(a.valorMax) : 'Sem limite'}</td>
                    <td>{a.slaDiasUteis ? `${a.slaDiasUteis} dia(s) útil(eis)` : '—'}</td>
                    <td>{a.titular}</td>
                    <td>{a.substituto || <span className='wf-danger-text'>—</span>}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => iniciarEdicao(a)}>
                        Editar
                      </button>
                      <button
                        className='wf-btn wf-btn--danger-outline wf-btn--sm'
                        disabled={excluindoId === a.id}
                        onClick={() => excluir(a)}
                      >
                        {excluindoId === a.id ? 'Excluindo...' : 'Excluir'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className='wf-card' style={{ marginTop: 16 }}>
        <div className='wf-card__header'>
          <div><h2>Painel de Integridade das Alçadas</h2><p>Saúde de cada nível hierárquico (N1–N7) — atualizado em tempo real.</p></div>
        </div>
        <div className='wf-table-wrap'>
          <table className='wf-table'>
            <thead>
              <tr>
                <th>Nível</th><th>Cargo</th><th>Usuários</th><th>Titular</th><th>Substituto</th>
                <th>Faixa de Valor</th><th>SLA</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {integridade.length === 0
                ? <tr><td colSpan={8} style={{ textAlign: 'center', color: '#a1a5b7' }}>Carregando integridade...</td></tr>
                : integridade.map(n => (
                  <tr key={n.nivel}>
                    <td><span className='wf-nivel-badge'>N{n.nivel}</span></td>
                    <td><strong>{n.nomeCargo}</strong></td>
                    <td style={{ textAlign: 'center' }}>{n.totalUsuarios}</td>
                    <td style={{ textAlign: 'center' }}>{n.temTitular ? '✓' : <span className='wf-danger-text'>✕</span>}</td>
                    <td style={{ textAlign: 'center' }}>{n.temSubstituto ? '✓' : <span style={{ color: '#ffa800' }}>—</span>}</td>
                    <td style={{ fontSize: 11 }}>
                      {n.valorMinimo != null
                        ? `R$ ${n.valorMinimo.toLocaleString('pt-BR')} → ${n.valorMaximo != null ? 'R$ ' + n.valorMaximo.toLocaleString('pt-BR') : '∞'}`
                        : '—'}
                    </td>
                    <td style={{ fontSize: 11 }}>{n.slaDiasUteis != null ? `${n.slaDiasUteis} dia(s) útil(eis)` : '—'}</td>
                    <td>
                      <span className={`wf-badge ${n.alertaStatus === 'OK' ? 'wf-badge--aprovada' : n.alertaStatus === 'ALERTA' ? 'wf-badge--em_analise' : 'wf-badge--rejeitada'}`}>
                        {n.alertaStatus === 'OK' ? 'Verificado' : n.alertaStatus === 'ALERTA' ? 'Pendente Substituto' : 'Risco de Governança'}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className='wf-card' style={{ marginTop: 16 }}>
        <div className='wf-card__header'>
          <div><h2>Matriz de SLA do Fechamento (D+5)</h2><p>SLA recomendado por etapa do processo — a etapa Aprovação segue a escada de alçada N1–N7 acima.</p></div>
        </div>
        <div className='wf-table-wrap'>
          <table className='wf-table'>
            <thead>
              <tr><th>Etapa</th><th>SLA da etapa</th><th>Limite acumulado (D+)</th><th /></tr>
            </thead>
            <tbody>
              {(() => {
                let acumulado = 0;
                return etapasSla.map(e => {
                  const isAprovacao = e.nomeEtapa === 'Aprovação';
                  if (!isAprovacao) acumulado += e.slaDiasUteis ?? 0;
                  return (
                    <tr key={e.etapaId}>
                      <td><strong>{e.ordemSequencial} — {e.nomeEtapa}</strong></td>
                      <td>
                        {editandoEtapaId === e.etapaId ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input type='number' min={0} value={slaEtapaForm} onChange={ev => setSlaEtapaForm(ev.target.value)} style={{ width: 60 }} />
                            <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={() => salvarSlaEtapa(e.etapaId)}>Salvar</button>
                            <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => setEditandoEtapaId(null)}>Cancelar</button>
                          </div>
                        ) : isAprovacao
                          ? <span style={{ color: '#a1a5b7' }}>Conforme alçada (N1–N7)</span>
                          : `${e.slaDiasUteis ?? 0} dia(s) útil(eis) / mesmo dia`}
                      </td>
                      <td>{isAprovacao ? '—' : `D+${acumulado}`}</td>
                      <td>
                        {!isAprovacao && editandoEtapaId !== e.etapaId && (
                          <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => { setEditandoEtapaId(e.etapaId); setSlaEtapaForm(String(e.slaDiasUteis ?? 0)); }}>Editar</button>
                        )}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ConfigAlcadasPage;
